/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useModalState } from '@core/configureStore';
import { closeModal, enqueueNotification } from '@core/features';
import { SocialPost, SocialPostStage } from '@core/models';
import { Button, Calendar, Divider, Modal, Popconfirm, Skeleton, Space, Tag } from 'antd';
import { MIDDLE_STYLE } from '@constants/responsive';
import moment from 'moment';
import { CalendarOutlined, DeleteOutlined } from '@ant-design/icons';
import { useHeroApiService } from '@core/hooks';
import LoadableContainer from '@components/LoadableContainer';
import { formatUtcTimestamp } from '@utils/date.util';
import { renderSocialPostStatus, renderStatusColor } from './shared';
import SocialPostImage from './SocialPostImage';
import { fetchSocialPosts } from '@core/features/studio/thunks';
import * as dayjs from 'dayjs';
import AnimatedComponent from '@components/AnimatedComponent';

type Props = {};

const SocialPostDetailModal = (props: Props) => {
  const dispatch = useAppDispatch();
  const heroApiService = useHeroApiService();
  const { socialPostModal, extraParams } = useModalState();
  const socialPost = useMemo<SocialPost | undefined>(() => extraParams?.socialPost, [extraParams]);
  const locationId = useMemo<string | undefined>(() => extraParams?.locationId, [extraParams]);
  const [calendarOpen, setCalendarOpen] = useState<boolean>(false);
  const [fetchedSocialPost, setFetchedSocialPost] = useState<SocialPost | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(true);

  const status = useMemo(() => renderSocialPostStatus(fetchedSocialPost), [fetchedSocialPost]);
  const [content, setContent] = useState<string>('');
  const [date, setDate] = useState<number>(moment().unix());

  const handleCloseModal = async () => {
    await dispatch(
      closeModal({
        modalName: 'socialPostModal',
      })
    );
  };

  const handleRegenerateContent = async () => {
    if (!fetchedSocialPost) return;
    setLoading(true);
    try {
      const content = await heroApiService.generateSocialPostContent(fetchedSocialPost.id);
      await dispatch(
        enqueueNotification({
          name: 'New post content generated!',
          description: 'Successfully generate content for social post',
          type: 'Success',
        })
      );
      setContent(content);
    } catch (error) {
      await dispatch(
        enqueueNotification({
          name: 'Generate social post content failed',
          description: 'Could not generate content for social post',
          type: 'Error',
        })
      );
    }
    setLoading(false);
  };

  const handleReschedulePost = () => {
    setCalendarOpen(true);
  };

  const onCalendarSelected = (day: dayjs.Dayjs) => {
    setDate(day.unix());
    setCalendarOpen(false);
  };

  const handleRemoveSocialPost = async () => {
    if (!fetchedSocialPost || !locationId) return;
    setLoading(true);
    await heroApiService.removeSocialPost(fetchedSocialPost.id);
    await dispatch(fetchSocialPosts({ locationId: locationId }));
    handleCloseModal();
    setLoading(false);
  };

  const handleSaveSocialPost = async () => {
    if (!fetchedSocialPost || !locationId) return;
    if (isUpdated) {
      try {
        await heroApiService.updateSocialPost(fetchedSocialPost.id, date, content);
        await dispatch(fetchSocialPosts({ locationId }));
        await handleRefresh();
        await dispatch(
          enqueueNotification({
            name: 'Social post is updated',
            description: 'Successfully updated social post',
            type: 'Success',
          })
        );
      } catch (error) {
        await dispatch(
          enqueueNotification({
            name: 'Failed to update social post',
            description: 'Could not update social post',
            type: 'Error',
          })
        );
      }
    }
    handleCloseModal();
  };

  const handleRefresh = async () => {
    if (!socialPost) return;
    setLoading(true);
    try {
      const _socialPost = await heroApiService.getSocialPostById(socialPost.id);
      setContent(_socialPost.content);
      if (_socialPost.publish_at) setDate(formatUtcTimestamp(_socialPost?.publish_at));
      setFetchedSocialPost(_socialPost);
    } catch (error) {
      console.log(error);
    }
    setLoading(false);
  };

  const isUpdated = useMemo(
    () =>
      formatUtcTimestamp(fetchedSocialPost?.publish_at) !== date ||
      fetchedSocialPost?.content !== content,
    [fetchedSocialPost, content, date]
  );

  useEffect(() => {
    const init = async () => {
      await handleRefresh();
    };
    init();
  }, [socialPost]);

  return (
    <Modal
      confirmLoading={loading}
      onCancel={handleCloseModal}
      onOk={handleSaveSocialPost}
      open={!!socialPost && socialPostModal}
      okText={isUpdated ? 'Save' : 'Done'}
      width={450}
      closable={false}>
      <LoadableContainer isLoading={!fetchedSocialPost || loading} loadComponent={<Skeleton />}>
        {status !== SocialPostStage.Published && (
          <React.Fragment>
            <Space>
              <Button onClick={handleReschedulePost}>
                {date ? (
                  <React.Fragment>
                    <CalendarOutlined /> {moment.unix(date).format('DD/MM/YYYY')}
                  </React.Fragment>
                ) : (
                  <React.Fragment>
                    <CalendarOutlined /> Schedule
                  </React.Fragment>
                )}
              </Button>
              <Button onClick={handleRegenerateContent}>Generate content</Button>
              <Popconfirm
                onConfirm={handleRemoveSocialPost}
                title="Delete the post"
                description="Are you sure to delete this post?"
                okButtonProps={{
                  size: 'middle',
                }}
                cancelButtonProps={{
                  size: 'middle',
                }}
                okText="Yes"
                cancelText="No">
                <Button danger>
                  <DeleteOutlined /> Remove
                </Button>
              </Popconfirm>
            </Space>
            <Divider />
          </React.Fragment>
        )}
        {calendarOpen ? (
          <AnimatedComponent.OpacityFadeInDiv delay={300}>
            <React.Fragment>
              <Calendar
                disabledDate={current => current.isBefore(dayjs.default().subtract(1, 'day'))}
                value={dayjs.unix(date)}
                fullscreen={false}
                onSelect={onCalendarSelected}
              />
              <Divider />
            </React.Fragment>
          </AnimatedComponent.OpacityFadeInDiv>
        ) : (
          <AnimatedComponent.OpacityFadeInDiv delay={300}>
            <React.Fragment>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Tag color={renderStatusColor(status)}>{status}</Tag>
                {fetchedSocialPost?.publish_at && (
                  <span>{moment.unix(date).format('DD/MM/YYYY HH:mm:ss')}</span>
                )}
              </div>
              <Divider style={{ margin: '10px 0px' }} />
              <p style={{ marginBottom: 20 }}>{content}</p>
              {locationId && (
                <SocialPostImage
                  locationId={locationId}
                  mediaId={fetchedSocialPost?.mediaIds[0] as any}
                />
              )}
              <div
                style={{
                  ...MIDDLE_STYLE,
                  justifyContent: 'flex-end',
                  height: '100%',
                  flexDirection: 'column',
                  padding: '0px 20px',
                }}>
                <Divider />
              </div>
            </React.Fragment>
          </AnimatedComponent.OpacityFadeInDiv>
        )}
      </LoadableContainer>
    </Modal>
  );
};

export default SocialPostDetailModal;
